import { Nullable, Selectable, ShallowDehydrateObject } from 'kysely'
import * as z from 'zod'
import { GetApiContract, PatchApiContract, PostApiContract, PutApiContract } from './ApiContract.js'
import { Fees, InvestorsWarrants, Issues, Sources, Warrants, WarrantsOrigins, WarrantsSubPeriods } from '../../db.js'
import { schemaBoolish } from './zodUtils.js'
import { putSourceSchema } from './SourcesApiContract.js'
import { AdvisorListItem } from '../AdvisorEntity.js'
import { feePutSchema } from './utils.js'
import { putInvestorSchema } from './InvestorsApiContract.js'

export type WarrantSubscriptionPeriod = ShallowDehydrateObject<
    Pick<
        Selectable<WarrantsSubPeriods>,
        | 'id'
        | 'subStartDate'
        | 'subEndDate'
        | 'warrantsRequired'
        | 'sharesReceived'
        | 'isVwap'
        | 'vwapDiscount'
        | 'vwapDays'
        | 'vwapPeriodEnd'
        | 'priceMinQuota'
        | 'priceMin'
        | 'priceMax'
        | 'noPriceMax'
        | 'warrantSubPrice'
        | 'commitments'
        | 'guarantors'
        | 'sharesSubscribed'
        | 'guarantorAllocation'
        | 'guarantorRemunerationShares'
        | 'createdAt'
        | 'updatedAt'
    >
>

export type WarrantSubscriptionPeriodWithPrelPriceEntity = WarrantSubscriptionPeriod & {
    prelSubPrice: number | null
}

export type WarrantOriginLight = Pick<
    Selectable<WarrantsOrigins>,
    'id' | 'issueId' | 'warrantsPerUnit' | 'numberOfWarrants' | 'externalComment'
> &
    Pick<Selectable<Issues>, 'statusStage' | 'type'> & { publishedDate: Date | null }

type WarrantOrigin = Pick<
    Selectable<WarrantsOrigins>,
    'id' | 'issueId' | 'convertibleId' | 'warrantsPerUnit' | 'numberOfWarrants'
> & { externalComment: string | null } & ShallowDehydrateObject<
        Nullable<
            Pick<
                Selectable<Issues>,
                | 'shareId'
                | 'type'
                | 'statusStage'
                | 'statusComplete'
                | 'statusMissingFields'
                | 'statusHideUntil'
                | 'generalMeetingDate'
                | 'incRightsDate'
                | 'exRightsDate'
                | 'recordDate'
                | 'rightsStartDate'
                | 'rightsEndDate'
                | 'subStartDate'
                | 'subEndDate'
                | 'sharesRequired'
                | 'rightsReceived'
                | 'rightsRequired'
                | 'unitsReceived'
                | 'sharesInUnit'
                | 'unitSubPrice'
                | 'outstandingShares'
                | 'offeredUnits'
                | 'offeredUnitsUnlisted'
                | 'abbTarget'
                | 'approxTotalCommitment'
                | 'approxCommitment'
                | 'approxGuarantor'
                | 'overAllotmentSize'
                | 'isinRights'
                | 'isinUnits'
                | 'noncashConsideration'
                | 'unitSubTotal'
                | 'unitSubWithRights'
                | 'unitSubWithoutRights'
                | 'unitSubGuarantor'
                | 'unitPctSubWithRights'
                | 'unitPctSubWithoutRights'
                | 'unitPctSubGuarantor'
                | 'overAllotmentAllocation'
                | 'secondaryOffering'
                | 'secondarySub'
                | 'noFinancialAdvisor'
                | 'noIssuingAgent'
                | 'noLegalAdvisor'
                | 'noOverAllotment'
                | 'noTermsInitially'
                | 'noSecondaryOffering'
                | 'noGuarantors'
                | 'noIsinUnits'
                | 'noInvestorsNamed'
            >
        >
    > &
    ShallowDehydrateObject<{
        publicationDate: Date | null
        warrants: Array<
            Pick<Selectable<Warrants>, 'id' | 'name'> &
                Pick<Selectable<WarrantsOrigins>, 'warrantsPerUnit'> & { instrumentName: string | null }
        >
        sources: Array<ShallowDehydrateObject<SourceListItemWithInternalComment>>
    }>

export type SourceListItemBase = ShallowDehydrateObject<
    Pick<Selectable<Sources>, 'id' | 'title' | 'sourceType' | 'sourceUrl' | 'publishedDate' | 'prId'>
>

export type SourceListItemWithInternalComment = SourceListItemBase & Pick<Selectable<Sources>, 'internalComment'>

export type WarrantListItem = Pick<
    Selectable<Warrants>,
    | 'id'
    | 'shareId'
    | 'instrumentId'
    | 'name'
    | 'statusHideUntil'
    | 'isinWarrant'
    | 'issuesAdjustPrice'
    | 'cancelled'
    | 'internalComment'
    | 'externalComment'
    | 'unlisted'
    | 'createdAt'
    | 'updatedAt'
> & {
    companyId: string | null
    companyName: string | null
    instrumentName: string | null
    instrumentIsin: string | null
    instrumentCreatedAt: Date | null
    instrumentSymbol: string | null
    closePrice: string | null
    marketplaceId: string | null
    marketplaceName: string | null
    sectorLevel1: string | null
    sectorLevel2: string | null
    publicationDate: Date | null
    currency: string | null
    totalNumberOfWarrants: string | number | bigint | null
    subscriptionPeriods: Array<WarrantSubscriptionPeriodWithPrelPriceEntity>
    origins: Array<WarrantOriginLight> | null
    financialAdvisors: Array<AdvisorListItem>
}

export interface GetWarrantListRequest extends GetApiContract {
    Response: Array<WarrantListItem>
    ResponseJson: Array<WarrantListItem>
}

// getWarrantsQuery
export type WarrantLightListItem = Omit<
    WarrantListItem,
    | 'instrumentCreatedAt'
    | 'closePrice'
    | 'marketplaceId'
    | 'marketplaceName'
    | 'sectorLevel1'
    | 'sectorLevel2'
    | 'publicationDate'
    | 'origins'
    | 'financialAdvisors'
>

export type WarrantInvestorListItem = Pick<
    Selectable<InvestorsWarrants>,
    'id' | 'investorAliasId' | 'investorId' | 'amountInCash' | 'feeLevel' | 'putOption' | 'primaryGuarantor'
> & { name: string | null }

export type FeeListItem = ShallowDehydrateObject<
    Pick<Selectable<Fees>, 'id' | 'feeCash' | 'feeUnits' | 'feeBoth' | 'feeLevel' | 'topDown' | 'middleDown'>
>

// getWarrantById
export type WarrantSingleItem = WarrantLightListItem & {
    isInWatchList: 1 | null
    closePrice: string | null
    totalNumberOfWarrants: string | number | bigint
    startTradeDate: Date | null
    origins: Array<WarrantOrigin>
    investors: Array<WarrantInvestorListItem>
    fees: Array<FeeListItem>
    sources: Array<SourceListItemWithInternalComment>
}

export interface GetWarrantByIdRequest extends GetApiContract {
    Response: WarrantSingleItem
    ResponseJson: WarrantSingleItem
    QueryParameters: {
        preferredLanguage: 'sv' | 'en'
    }
}

export interface GetWarrantsByCompanyIdRequest extends GetApiContract {
    Response: Array<WarrantLightListItem>
    ResponseJson: Array<WarrantLightListItem>
    QueryParameters: {
        companyId: string
        excludeCancelled?: 'true'
    }
}

export interface GetWarrantsByIssueIdRequest extends GetApiContract {
    Response: Array<WarrantLightListItem>
    ResponseJson: Array<WarrantLightListItem>
    QueryParameters: {
        issueId: string
    }
}

export const postWarrantSchema = z.object({
    name: z.string(),
    shareId: z.string(),
    isinWarrant: z.string().nullish(),
    unlisted: schemaBoolish.nullish(),
    internalComment: z.string().nullish(),
    externalComment: z.string().nullish(),
})

export interface PostWarrantRequest extends PostApiContract {
    Body: z.infer<typeof postWarrantSchema>
    Response: z.infer<typeof postWarrantSchema>
    ResponseJson: z.infer<typeof postWarrantSchema>
}

export const putWarrantSchema = postWarrantSchema.extend({
    id: z.string(),
    instrumentId: z.string().nullish(),
    statusHideUntil: z.iso.datetime().nullish(),
    issuesAdjustPrice: schemaBoolish.nullish(),
    cancelled: schemaBoolish.nullish(),
    origins: z.array(
        z.object({
            issueId: z.string().nullish(),
            convertibleId: z.string().nullish(),
            warrantsPerUnit: z.number().nullish(),
            numberOfWarrants: z.number().nullish(),
            externalComment: z.string().nullish(),
        }),
    ),
    subscriptionPeriods: z.array(
        z.object({
            warrantId: z.string(),
            subStartDate: z.iso.date().nullish(),
            subEndDate: z.iso.date().nullish(),
            warrantsRequired: z.number().nullish(),
            sharesReceived: z.number().nullish(),
            isVwap: schemaBoolish.nullish(),
            vwapDiscount: z.number().nullish(),
            vwapDays: z.number().nullish(),
            vwapPeriodEnd: z.iso.date().nullish(),
            priceMinQuota: schemaBoolish.nullish(),
            priceMin: z.number().nullish(),
            priceMax: z.number().nullish(),
            noPriceMax: schemaBoolish.nullish(),
            warrantSubPrice: z.number().nullish(),
            commitments: z.number().nullish(),
            guarantors: z.number().nullish(),
            sharesSubscribed: z.number().nullish(),
            guarantorAllocation: z.number().nullish(),
            guarantorRemunerationShares: z.number().nullish(),
        }),
    ),
    investors: z.array(putInvestorSchema),
    fees: z.array(feePutSchema),
    sources: z.array(putSourceSchema),
})

export interface PutWarrantRequest extends PutApiContract {
    Body: z.infer<typeof putWarrantSchema>
    Response: z.infer<typeof putWarrantSchema>
    ResponseJson: z.infer<typeof putWarrantSchema>
}

export const patchWarrantSchema = z.object({
    isinWarrant: z.string(),
})

export interface PatchWarrantRequest extends PatchApiContract {
    Body: z.infer<typeof patchWarrantSchema>
    Response: z.infer<typeof patchWarrantSchema>
    ResponseJson: z.infer<typeof patchWarrantSchema>
}
